import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { EntityInteractionEventType } from '@puppycad/cad-core';
import type { Subscription } from 'rxjs';
import * as THREE from 'three';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObjects,
} from './viewer-selection-helpers';

/**
 * 多物体框选状态 Store。
 * 职责：
 * 1. 维护多实体选择状态（含框选与 Ctrl+点击增量选择）；
 * 2. 统一订阅多物体框选与选择清空事件；
 * 3. 由外部在 Viewer 就绪后显式调用 subscribe() 完成初始化。
 */
export const useGetMultiSelect = defineStore('get-multi-select', () => {
  /** 是否处于多实体关系面板候选态（当前选中数量大于 1）。 */
  const multipleEntitiesBoxSelected = ref(false);
  /** 框选到的物体列表。 */
  // 3D Object 必须保持原始引用，避免 Vue 深层代理破坏 three.js 内部状态与性能。
  const multipleSelectedObjects = shallowRef<THREE.Object3D[]>([]);
  /** 框选到的物体 ID 列表。 */
  const multipleSelectedObjectIds = ref<string[]>([]);
  /** 是否为 crossing 框选（右往左）。 */
  const multipleSelectedIsCrossing = ref(false);
  /** 内部订阅列表。 */
  let subscriptions: Subscription[] = [];
  /** 是否已完成事件绑定。 */
  let hasSubscribed = false;
  const { getViewer } = useViewer();


  /**
   * 重置所有多选状态。
   */
  function reset(): void {
    multipleEntitiesBoxSelected.value = false;
    multipleSelectedObjects.value = [];
    multipleSelectedObjectIds.value = [];
    multipleSelectedIsCrossing.value = false;
  }

  /**
   * 同步当前已选中的对象状态。
   */
  function syncCurrentSelectionState(): void {
    const viewer = getViewer();
    if (!viewer) {
      reset();
      return;
    }
    const selectedObjects = resolveViewerSelectedObjects(viewer);
    const selectedObjectIds = viewer.getSelectedObjectIds();
    //------ 几何关系面板多选态收口 开始------
    // 统一以“当前已选对象数量 > 1”作为几何关系面板候选态，
    // 让框选与 Ctrl+点击增量选择都走同一显示语义。
    multipleEntitiesBoxSelected.value = selectedObjects.length > 1;
    //------ 几何关系面板多选态收口 结束------
    multipleSelectedObjects.value = selectedObjects;
    multipleSelectedObjectIds.value = selectedObjectIds;
  }

  /**
   * 取消所有事件订阅。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSubscribed = false;
  }

  /**
   * 尝试订阅 Viewer 暴露的统一交互事件。
   * @returns 是否绑定成功。
   */
  function subscribe(): void {
    if (hasSubscribed) {
      return;
    }
    const viewer = getViewer();
    const entityBoxSelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.EntityBoxSelected);
    const multipleEntitiesBoxSelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.MultipleEntitiesBoxSelected);
    const entitySelected$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.EntitySelected);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    if (!entityBoxSelected$ || !multipleEntitiesBoxSelected$ || !entitySelected$ || !selectionCleared$) {
      return;
    }

    subscriptions = [
      entityBoxSelected$.subscribe((payload: {
        selectedObjects: THREE.Object3D[];
        selectedObjectIds: string[];
        isCrossing: boolean;
      }) => {
        // 框选事件命中多个对象时进入几何关系面板候选态。
        multipleEntitiesBoxSelected.value = payload.selectedObjects.length > 1;
        multipleSelectedObjects.value = payload.selectedObjects;
        multipleSelectedObjectIds.value = payload.selectedObjectIds;
        multipleSelectedIsCrossing.value = payload.isCrossing;
      }),
      multipleEntitiesBoxSelected$.subscribe((payload: {
        selectedObjects: THREE.Object3D[];
        selectedObjectIds: string[];
        isCrossing: boolean;
      }) => {
        // 点击联动多选会走该事件；统一按“选中数量 > 1”维护候选态。
        multipleEntitiesBoxSelected.value = payload.selectedObjects.length > 1;
        multipleSelectedObjects.value = payload.selectedObjects;
        multipleSelectedObjectIds.value = payload.selectedObjectIds;
        multipleSelectedIsCrossing.value = payload.isCrossing;
      }),
      entitySelected$.subscribe(() => {
        // 任何选择变化都按当前快照重算候选态，避免仅框选才能进入几何关系面板。
        syncCurrentSelectionState();
      }),
      selectionCleared$.subscribe(() => {
        reset();
      }),
    ];

    hasSubscribed = true;
    syncCurrentSelectionState();
  }

  return {
    multipleEntitiesBoxSelected,
    multipleSelectedObjects,
    multipleSelectedObjectIds,
    multipleSelectedIsCrossing,
    reset,
    subscribe,
    unsubscribe,
  };
});
